import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  House,
  Buildings,
  Users,
  Database,
  FileText,
  Bell,
  Gear,
  SignOut,
  List,
  CaretLeft,
  CaretRight,
  ChartBar,
  Trophy,
  Lightning,
  Link as LinkIcon,
  LinkSimple,
  Palette,
  Envelope,
  ShieldCheck,
  Clock,
  Export,
  Archive,
  Check,
  CheckCircle
} from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Layout = ({ children }) => {
  const { user, logout, appSettings } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [bellOpen, setBellOpen] = useState(false);
  const bellRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/notifications`);
      setNotifications(data.slice(0, 8));
      setUnreadCount(data.filter(n => !n.is_read).length);
    } catch {}
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Close bell dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (bellRef.current && !bellRef.current.contains(e.target)) {
        setBellOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleMarkRead = async (notifId) => {
    try {
      await axios.put(`${API_URL}/api/notifications/${notifId}/read`);
      fetchNotifications();
    } catch {}
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.put(`${API_URL}/api/notifications/read-all`);
      fetchNotifications();
    } catch {}
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/dashboard', icon: House, label: 'Dashboard', roles: ['super_admin', 'branch_admin', 'branch_user'] },
    { path: '/branches', icon: Buildings, label: 'Branches', roles: ['super_admin'] },
    { path: '/users', icon: Users, label: 'Users', roles: ['super_admin', 'branch_admin'] },
    { path: '/data-structures', icon: Database, label: 'Data Structures', roles: ['super_admin'] },
    { path: '/records', icon: FileText, label: 'Records', roles: ['super_admin', 'branch_admin', 'branch_user'] },
    { path: '/records/archived', icon: Archive, label: 'Archived', roles: ['super_admin'] },
    { path: '/reminders', icon: Clock, label: 'Reminders', roles: ['super_admin', 'branch_admin', 'branch_user'] },
    { path: '/temporary-links', icon: LinkIcon, label: 'Temp Links', roles: ['super_admin', 'branch_admin', 'branch_user'] },
    { path: '/reports', icon: ChartBar, label: 'Reports', roles: ['super_admin', 'branch_admin'] },
    { path: '/leaderboard', icon: Trophy, label: 'Leaderboard', roles: ['super_admin', 'branch_admin'] },
    { path: '/allocation-rules', icon: Lightning, label: 'Allocation Rules', roles: ['super_admin', 'branch_admin'] },
  ];

  const settingsItems = [
    { path: '/settings/statuses', icon: List, label: 'Statuses', roles: ['super_admin'] },
    { path: '/settings/webhooks', icon: LinkSimple, label: 'Webhooks', roles: ['super_admin'] },
    { path: '/settings/themes', icon: Palette, label: 'Themes', roles: ['super_admin'] },
    { path: '/settings/smtp', icon: Envelope, label: 'SMTP', roles: ['super_admin'] },
    { path: '/settings/ip-whitelist', icon: ShieldCheck, label: 'IP Whitelist', roles: ['super_admin'] },
    { path: '/settings/app', icon: Gear, label: 'App Settings', roles: ['super_admin'] },
    { path: '/settings/audit-logs', icon: Export, label: 'Audit Logs', roles: ['super_admin'] },
  ];

  const filteredNavItems = navItems.filter(item => item.roles.includes(user?.role));
  const filteredSettingsItems = settingsItems.filter(item => item.roles.includes(user?.role));

  const NavLink = ({ item }) => {
    const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
    const Icon = item.icon;
    
    return (
      <Link
        to={item.path}
        className={`sidebar-link ${isActive ? 'active' : ''}`}
        onClick={() => setMobileMenuOpen(false)}
      >
        <Icon size={20} weight={isActive ? 'fill' : 'regular'} />
        {!sidebarCollapsed && <span>{item.label}</span>}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed md:sticky top-0 left-0 h-screen bg-background border-r border-border z-50
        transition-all duration-300 flex flex-col
        ${sidebarCollapsed ? 'w-16' : 'w-64'}
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-border">
          {!sidebarCollapsed && (
            <div className="flex flex-col">
              <h1 className="font-bold text-lg text-foreground">{appSettings?.app_name || 'Branch Manager'}</h1>
              <span className="text-xs text-muted-foreground">{appSettings?.tagline || ''}</span>
            </div>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="hidden md:flex"
            data-testid="sidebar-toggle"
          >
            {sidebarCollapsed ? <CaretRight size={16} /> : <CaretLeft size={16} />}
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          {filteredNavItems.map(item => (
            <NavLink key={item.path} item={item} />
          ))}
          
          {filteredSettingsItems.length > 0 && (
            <>
              <div className="pt-4 pb-2">
                {!sidebarCollapsed && (
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-3">
                    Settings
                  </span>
                )}
              </div>
              {filteredSettingsItems.map(item => (
                <NavLink key={item.path} item={item} />
              ))}
            </>
          )}
        </nav>

        {/* User section */}
        <div className="border-t border-border p-3">
          <div className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'}`}>
            <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            {!sidebarCollapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
            )}
          </div>
          <div className={`flex ${sidebarCollapsed ? 'justify-center mt-2' : 'gap-2 mt-3'}`}>
            <Link to="/profile">
              <Button variant="ghost" size={sidebarCollapsed ? 'icon' : 'sm'} className="w-full" data-testid="profile-link">
                <Gear size={16} />
                {!sidebarCollapsed && <span className="ml-2">Profile</span>}
              </Button>
            </Link>
            {!sidebarCollapsed && (
              <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
                <SignOut size={16} />
                <span className="ml-2">Logout</span>
              </Button>
            )}
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top bar */}
        <header className="h-16 bg-background border-b border-border sticky top-0 z-30 flex items-center justify-between px-4 md:px-6">
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setMobileMenuOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <List size={24} />
          </Button>
          
          <div className="flex-1" />
          
          <div className="flex items-center gap-2">
            <div className="relative" ref={bellRef}>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setBellOpen(!bellOpen)}
                data-testid="notifications-btn"
              >
                <Bell size={20} />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground px-1" data-testid="notification-badge">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </Button>

              {bellOpen && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-card border border-border rounded-lg shadow-lg z-50 overflow-hidden animate-slide-down" data-testid="notification-dropdown">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                    <h3 className="text-sm font-semibold">Notifications</h3>
                    {unreadCount > 0 && (
                      <button
                        onClick={handleMarkAllRead}
                        className="text-xs text-primary hover:underline"
                        data-testid="mark-all-read-btn"
                      >
                        Mark all read
                      </button>
                    )}
                  </div>

                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length > 0 ? notifications.map(n => (
                      <div
                        key={n.id}
                        className={`flex items-start gap-3 px-4 py-3 border-b border-border/50 cursor-pointer hover:bg-secondary/50 transition-colors ${!n.is_read ? 'bg-primary/5' : ''}`}
                        onClick={() => {
                          if (!n.is_read) handleMarkRead(n.id);
                          setBellOpen(false);
                          if (n.link) navigate(n.link);
                        }}
                        data-testid={`notification-item-${n.id}`}
                      >
                        <div className="mt-0.5">
                          {!n.is_read ? (
                            <div className="w-2 h-2 rounded-full bg-primary mt-1.5" />
                          ) : (
                            <CheckCircle size={14} className="text-muted-foreground mt-0.5" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm leading-snug ${!n.is_read ? 'font-medium' : 'text-muted-foreground'}`}>
                            {n.message}
                          </p>
                          <p className="text-[11px] text-muted-foreground mt-1">
                            {n.created_at ? new Date(n.created_at).toLocaleString() : ''}
                          </p>
                        </div>
                      </div>
                    )) : (
                      <div className="py-8 text-center text-sm text-muted-foreground">
                        <Bell size={24} className="mx-auto mb-2 opacity-40" />
                        No notifications yet
                      </div>
                    )}
                  </div>

                  <Link
                    to="/notifications"
                    className="block px-4 py-2.5 text-center text-xs font-medium text-primary hover:bg-secondary/50 border-t border-border"
                    onClick={() => setBellOpen(false)}
                    data-testid="view-all-notifications"
                  >
                    View all notifications
                  </Link>
                </div>
              )}
            </div>
            <Badge variant="outline" className="hidden sm:inline-flex">
              {user?.role?.replace('_', ' ')}
            </Badge>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 md:p-6 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
